/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import {
  MenuItem,
  Select,
  IconButton,
  Popover,
  TextField,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Tooltip,
  FormLabel,
  Checkbox, // Add this import
} from '@material-ui/core';
import ArchiveIcon from '@material-ui/icons/Archive'; // Add this import near other icon imports
import EditIcon from '@material-ui/icons/Edit';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import ForumIcon from '@material-ui/icons/Forum';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import ChatIcon from '@material-ui/icons/Chat';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'; // Import the horizonatal kebab icon
import IconClose from 'mdi-material-ui/Close';
import DotsVerticalCircleOutline from 'mdi-material-ui/DotsVerticalCircleOutline';
import PencilBoxOutline from 'mdi-material-ui/PencilBoxOutline';
import { makeStyles } from '@material-ui/core/styles';
import { remove } from 'lodash';
import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'redux-first-router-link';
import { replace } from 'redux-first-router';

import { AppContextSwitcher, checkAdminRole } from '@marapp/earth-shared';
//import { WorkspaceContext } from '../../utils/contexts/WorkspaceContext';

import {
  AI_API_URL,
  COMPANY_ABOUT_URL,
  COMPANY_SUPPORT_URL,
  COMPANY_URL,
  MAP_ADMIN_URL,
  MAP_APP_NAME,
} from '../../config';
import { EPanels } from '../../modules/sidebar/model';
import OrganizationService from '../../services/OrganizationService';
import { APP_LOGO } from '../../theme';
import { Auth0Context } from '../../utils/contexts';
import { WorkspaceListItem } from './WorkspaceListItem';
import { getWorkspaceLogo } from '../../theme';

// Add this console log right after imports to verify the function is imported
// console.log('getWorkspaceLogo function:', getWorkspaceLogo);

const { Option } = AppContextSwitcher;

const useStyles = makeStyles((theme) => ({
  logo: {
    border: 'none !important',
  },
}));

interface IUser {
  allGroups?: string[];
  email?: string;
  organization?: string;
  // Add other properties as needed
}

interface IProps {
  label?: string;
  checkedCount?: number;
  renderButton?: boolean;
  renderDropdown?: boolean;
  onChange?: (g: any) => void;
  handleResetLocation?: () => void;
  threads: any[];
  setThreads: Function;
  selectedThread: string;
  setPlacesSearch?: Function;
  setWorkspaces?: Function;
  resetMap?: Function; // Add this line
  setLayersSearch?: Function;
  setSidebarPanel?: Function;
  resetLayers?: Function;
  setNotification?: Function;
  handleThreadChange: (event: any) => void;
  handleClick: (url: URL) => void;
}

interface ThreadMetadata {
  thread_id: string;
  created_at: string;
  updated_at: string;
  last_viewed: string;
  href: string;
  label: string;
  metadata: Record<string, any>;
  organization: string;
}

const Header = (props: IProps) => {
  const { t } = useTranslation();
  const {
    roles,
    userData,
    isAuthenticated,
    getAccessToken,
    groups,
    selectedWorkspace, // This is an array like ['workspaceName']
  } = useContext(Auth0Context);

  // Remove the old selectedGroup-related state and logic
  // const hasMultipleGroups = userData.allGroups?.length > 1;
  // const allInitiallySelected = selectedGroup?.length === userData.allGroups?.length;
  // const [selectedGroups, setSelectedGroups] = useState(
  //   allInitiallySelected ? userData.allGroups : selectedGroup || []
  // );

  const {
    selectedThread,
    resetMap,
    setPlacesSearch,
    setWorkspaces,
    setLayersSearch,
    setSidebarPanel,
    resetLayers,
    handleThreadChange,
    handleClick,
  } = props;

  const [availableGroups, setAvailableGroups] = useState(
    groups.map((group) => ({ name: group, layers: 'N/A', locations: 'N/A' }))
  );
  const [token, setToken] = useState('');
  const [threads, setThreads] = useState([]);
  const [editAnchorEl, setEditAnchorEl] = useState<null | HTMLElement>(null);
  const [editThread, setEditThread] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [isEditPopoverOpen, setIsEditPopoverOpen] = useState(false);
  const [shouldOpenRenameDialog, setShouldOpenRenameDialog] = useState(false);
  const [shouldOpenOrgDialog, setShouldOpenOrgDialog] = useState(false);
  const [orgAnchorEl, setOrgAnchorEl] = useState<null | HTMLElement>(null);
  const [orgToSet, setOrgToSet] = useState<string>('');
  const [hideArchived, setHideArchived] = useState<boolean>(true); // Add this state
  const hasAutoSelectedRef = useRef(false);
  const isFetchingRef = useRef(false);

  const threadIds = threads.map((t) => t.thread_id);
  const selectValue = threadIds.includes(selectedThread) ? selectedThread : '';

  useEffect(() => {
    (async () => {
      try {
        const response = await OrganizationService.fetchStats({ group: groups.join(',') });
        setAvailableGroups(response.data);
        // Remove this line - Header shouldn't set workspaces based on userData
        // setWorkspaces(userData.allGroups);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  useEffect(() => {
    async function fetchToken() {
      const accessToken = await getAccessToken();
      setToken(accessToken);
    }
    if (isAuthenticated) {
      fetchToken();
    }
  }, [isAuthenticated, getAccessToken]);

  const handleEditClick = (thread, event) => {
    setEditThread(thread);
    setNewLabel(thread.label || '');
    setIsEditPopoverOpen(true);
  };

  const handleEditClose = () => {
    setEditThread(null);
    setEditAnchorEl(null);
    setIsEditPopoverOpen(false);
  };

  const handleEditSave = async () => {
    const updateBody = {
      metadata: {
        ...editThread.metadata,
        label: newLabel,
        // updated_at: new Date().toISOString(), // This will format as: 2025-11-10T16:11:22.840Z
      },
    };

    // console.log('🔍 updateThread - Request URL:', `${AI_API_URL}/threads/${editThread.thread_id}`);
    // console.log('🔍 updateThread - Request body:', JSON.stringify(updateBody, null, 2));

    const response = await fetch(`${AI_API_URL}/threads/${editThread.thread_id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateBody),
    });

    // console.log('🔍 updateThread - Response status:', response.status);
    // console.log(
    //   '🔍 updateThread - Response headers:',
    //   Object.fromEntries(response.headers.entries())
    // );

    if (response.ok) {
      const responseData = await response.json();
      // console.log('🔍 updateThread - Response data:', JSON.stringify(responseData, null, 2));
    } else {
      const errorText = await response.text();
      console.error('🔍 updateThread - Error response:', errorText);
    }

    handleEditClose(); // Close the popover
    fetchThreads(); // Refresh threads
  };

  // Extract the first workspace from the array
  const currentWorkspace = Array.isArray(selectedWorkspace)
    ? selectedWorkspace[0]
    : selectedWorkspace;

  const setThreadIdInUrl = (threadId: string | null) => {
    const params = new URLSearchParams(window.location.search);
    if (threadId) {
      params.set('thread_id', threadId);
    } else {
      params.delete('thread_id');
    }
    params.delete('shared'); // always strip the transient share marker
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  };

  const fetchThreads = async (workspace = currentWorkspace, skipArchived = hideArchived) => {
    // Prevent duplicate concurrent calls during initial load. Once initial selection is
    // complete (hasAutoSelectedRef = true), user-triggered refreshes always proceed.
    if (isFetchingRef.current && !hasAutoSelectedRef.current) return;
    isFetchingRef.current = true;
    try {
      const workspaceToUse = workspace || (userData as IUser).organization;

      const requestBody = {
        metadata: {
          user_id: userData.email,
          organization: workspaceToUse,
        },
        limit: 21,
        offset: 0,
        sort_by: 'updated_at',
        sort_order: 'desc',
        select: ['thread_id', 'created_at', 'updated_at', 'metadata'],
      };

      const response = await fetch(`${AI_API_URL}/threads/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        console.warn(`Failed to fetch threads. Status: ${response.status}`);
        return;
      }

      // Log the size of each thread record
      data.forEach((thread, index) => {
        const threadJson = JSON.stringify(thread);
        const sizeInBytes = new Blob([threadJson]).size;
        const sizeInKB = (sizeInBytes / 1024).toFixed(2);
        // console.log(
        //   `DEBUG header: thread ${index + 1} label:${thread.metadata?.label || ''} id:${
        //     thread.thread_id
        //   }: ${sizeInKB} kB`
        // );

        // Log size of each top-level key in thread
        const threadKeys = Object.keys(thread);
        const threadKeySizes = threadKeys.map((key) => {
          const valueJson = JSON.stringify(thread[key]);
          const valueSizeKB = (new Blob([valueJson]).size / 1024).toFixed(2);
          return `${key}: ${valueSizeKB} kB`;
        });
        // console.log(`DEBUG header: thread keys: ${threadKeySizes.join(', ')}`);

        // Log size of each values key if it exists
        if (thread.values) {
          const valuesKeys = Object.keys(thread.values);
          const valuesSizes = valuesKeys.map((key) => {
            const valueJson = JSON.stringify(thread.values[key]);
            const valueSizeKB = (new Blob([valueJson]).size / 1024).toFixed(2);
            return `${key}: ${valueSizeKB} kB`;
          });
          console.log(`DEBUG header: values keys: ${valuesSizes.join(', ')}`);
        }

        // Log size of each metadata key
        if (thread.metadata) {
          const metadataKeys = Object.keys(thread.metadata);
          const metadataSizes = metadataKeys.map((key) => {
            const valueJson = JSON.stringify(thread.metadata[key]);
            const valueSizeKB = (new Blob([valueJson]).size / 1024).toFixed(2);
            return `${key}: ${valueSizeKB} kB`;
          });
          // console.log(`DEBUG header: metadata keys: ${metadataSizes.join(', ')}`);
        }
      });

      // Conditionally filter out archived threads based on skipArchived flag
      const filteredData = skipArchived
        ? data.filter((thread) => {
            const label = thread.metadata?.label || '';
            return !label.startsWith('[archived] ');
          })
        : data;

      if (!filteredData || filteredData.length === 0) {
        // Auto-create a new thread only if we're hiding archived (normal mode)
        if (skipArchived) {
          const createBody = {
            metadata: {
              label: new Date().toLocaleString(),
              user_id: userData.email,
              organization: workspaceToUse,
              href: `${window.location.origin}/earth`,
              last_viewed: new Date().toISOString(),
            },
          };

          try {
            const createResponse = await fetch(`${AI_API_URL}/threads`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(createBody),
            });

            if (createResponse.ok) {
              const newThreadData = await createResponse.json();
              const newThreadId = newThreadData['thread_id'];

              const newThread: ThreadMetadata = {
                thread_id: newThreadId,
                created_at: newThreadData.created_at,
                updated_at: newThreadData.updated_at,
                last_viewed: newThreadData.updated_at,
                href: createBody.metadata.href,
                label: createBody.metadata.label,
                metadata: createBody.metadata,
                organization: createBody.metadata.organization,
              };

              setThreads([newThread]);

              setThreadIdInUrl(newThreadId);
              handleThreadChange({
                target: { value: newThreadId },
              });
            } else {
              console.error('Failed to create new thread');
              const errorText = await createResponse.text();
              console.error('🔍 createThread - Error response:', errorText);
              setThreads([]);
            }
          } catch (createError) {
            console.error('Error creating new thread:', createError);
            setThreads([]);
          }
        } else {
          // When showing archived, just set empty array if no results
          setThreads([]);
        }
      } else {
        const fetchedThreads: ThreadMetadata[] = filteredData.map((thread: ThreadMetadata) => ({
          thread_id: thread.thread_id,
          created_at: thread.created_at,
          updated_at: thread.updated_at,
          last_viewed: thread.metadata.last_viewed ?? thread.updated_at, // fallback to updated_at if last_viewed is not populated
          href: thread.metadata.href,
          label: thread.metadata.label,
          metadata: thread.metadata,
          organization: thread.metadata.organization,
        }));

        fetchedThreads.sort((a, b) => (a.last_viewed < b.last_viewed ? 1 : -1));

        setThreads(fetchedThreads);

        if (fetchedThreads.length > 0) {
          const mostRecentThread = fetchedThreads[0];

          // On initial load, check for a thread_id in the URL, then fall back to shared-link logic.
          if (!hasAutoSelectedRef.current) {
            hasAutoSelectedRef.current = true;

            const currentParams = new URLSearchParams(window.location.search);
            const urlThreadId = currentParams.get('thread_id');

            if (urlThreadId) {
              // Check ownership via a targeted API call — the base fetchThreads uses limit:21
              // sorted by updated_at, so older threads may not appear in that list.
              const ownedThread = await fetchThreadById(urlThreadId);
              if (ownedThread) {
                // Thread belongs to this user; merge into list if outside the fetch window.
                if (!fetchedThreads.find((t) => t.thread_id === urlThreadId)) {
                  setThreads([ownedThread, ...fetchedThreads]);
                }
                setThreadIdInUrl(urlThreadId);
                handleThreadChange({ target: { value: urlThreadId } });
                return;
              }
              // Thread not owned by this user (shared link) — strip thread_id and fall through.
              setThreadIdInUrl(null);
            }

            const isSharedLink =
              currentParams.has('shared') ||
              currentParams.has('layers') ||
              currentParams.has('coordinates') ||
              currentParams.has('basemap') ||
              currentParams.has('geojson');

            if (isSharedLink) {
              const organization = currentWorkspace || (userData as any)?.organization;
              // Strip the transient 'shared' marker from the href saved to thread metadata
              const cleanParams = new URLSearchParams(window.location.search);
              cleanParams.delete('shared');
              const hrefToSave = `${window.location.origin}${
                window.location.pathname
              }?${cleanParams.toString()}`;
              const newThreadLabel = new Date().toLocaleString();
              try {
                const response = await fetch(`${AI_API_URL}/threads`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    metadata: {
                      label: newThreadLabel,
                      user_id: userData.email,
                      organization,
                      href: hrefToSave,
                    },
                  }),
                });
                const newThreadData = await response.json();
                const newThread = {
                  thread_id: newThreadData.thread_id,
                  created_at: newThreadData.created_at || new Date().toISOString(),
                  updated_at: newThreadData.updated_at || new Date().toISOString(),
                  last_viewed: newThreadData.created_at || new Date().toISOString(),
                  href: hrefToSave,
                  label: newThreadData.metadata?.label || newThreadLabel,
                  metadata: newThreadData.metadata || { label: newThreadLabel, organization },
                  organization: newThreadData.metadata?.organization || organization,
                };
                setThreads([newThread, ...fetchedThreads]);
                setThreadIdInUrl(newThreadData.thread_id);
                handleThreadChange({ target: { value: newThreadData.thread_id } });
              } catch (error) {
                console.error('Error creating thread for shared link:', error);
                setThreadIdInUrl(mostRecentThread.thread_id);
                handleThreadChange({ target: { value: mostRecentThread.thread_id } });
              }
              return;
            }

            // Default: select the most-recent thread.
            // Inside the guard so concurrent fetchThreads calls don't override the selection
            // made by the shared-link or urlThreadId branches above.
            setThreadIdInUrl(mostRecentThread.thread_id);
            handleThreadChange({
              target: { value: mostRecentThread.thread_id },
            });
          }
        }
      }
    } catch (error) {
      console.warn('Error fetching threads:', error);
    } finally {
      isFetchingRef.current = false;
    }
  };

  const fetchThreadById = async (threadId: string): Promise<ThreadMetadata | null> => {
    try {
      const response = await fetch(`${AI_API_URL}/threads/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: [threadId],
          metadata: { user_id: userData.email },
        }),
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (!data?.length) return null;
      const thread = data[0];
      return {
        thread_id: thread.thread_id,
        created_at: thread.created_at,
        updated_at: thread.updated_at,
        last_viewed: thread.metadata?.last_viewed ?? thread.updated_at,
        href: thread.metadata?.href,
        label: thread.metadata?.label,
        metadata: thread.metadata,
        organization: thread.metadata?.organization,
      };
    } catch {
      return null;
    }
  };

  // Add handler for toggling archived view
  const handleArchivedToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value === 'active';
    setHideArchived(newValue);
    fetchThreads(currentWorkspace, newValue);
  };

  // Update useEffect to watch currentWorkspace changes
  useEffect(() => {
    if (currentWorkspace && isAuthenticated && userData?.email) {
      // console.log('Refetching threads for workspace change:', currentWorkspace);
      fetchThreads(currentWorkspace);
    }
  }, [currentWorkspace, isAuthenticated, userData?.email]);

  // Update existing useEffect to use currentWorkspace
  useEffect(() => {
    if (threads.length === 0) {
      fetchThreads(currentWorkspace);
    } else if (isChatModalOpen) {
      // Refetch to get fresh ordering by updated_at
      fetchThreads(currentWorkspace);
    }
  }, [isChatModalOpen, userData, threads.length === 0, currentWorkspace]);

  // Remove the old useEffect that auto-selected first thread
  // useEffect(() => {
  //   if (threads.length > 0 && !selectedThread) {
  //     handleThreadChange({
  //       target: { value: threads[0].thread_id },
  //     }); // Select the first thread by default
  //   }
  // }, [threads, selectedThread]);

  const handleCreateThread = async () => {
    // Save current thread state before creating new one
    const currentThreadId = selectedThread;
    if (currentThreadId) {
      try {
        await fetch(`${AI_API_URL}/threads/${currentThreadId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            metadata: {
              href: window.location.href,
            },
          }),
        });
      } catch (err) {
        console.error('Failed to save current thread state:', err);
      }
    }

    const createBody = {
      metadata: {
        label: new Date().toLocaleString(),
        user_id: userData.email,
        organization: currentWorkspace || (userData as IUser).organization,
        href: `${window.location.origin}/earth`,
      },
    };

    // console.log('🔍 handleCreateThread - Request URL:', `${AI_API_URL}/threads`);
    // console.log('🔍 handleCreateThread - Request body:', JSON.stringify(createBody, null, 2));

    fetch(`${AI_API_URL}/threads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createBody),
    })
      .then((response) => {
        // console.log('🔍 handleCreateThread - Response status:', response.status);
        // console.log(
        //   '🔍 handleCreateThread - Response headers:',
        //   Object.fromEntries(response.headers.entries())
        // );

        return response.json();
      })
      .then((data) => {
        // console.log('🔍 handleCreateThread - Response data:', JSON.stringify(data, null, 2));

        const newThreadId = data['thread_id'];
        handleThreadChange({ target: { value: newThreadId } });
        // hasFetchedThreads.current = false;
        fetchThreads();

        // Reset the map and set the URL with only thread_id (clean state for the new thread)
        resetLayers();
        replace(`/earth?thread_id=${newThreadId}`);
        resetMap();
      })
      .catch((error) => {
        console.error('🔍 handleCreateThread - Error:', error);
      });
    setIsChatModalOpen(false);
  };

  // Add this new function after handleCreateThread
  const handleArchiveThread = async () => {
    if (!selectedThread) return;

    const currentThread = threads.find((t) => t.thread_id === selectedThread);
    if (!currentThread) return;

    const currentLabel = currentThread.metadata?.label || currentThread.label || '';

    // Don't double-archive
    if (currentLabel.startsWith('[archived] ')) return;

    const updateBody = {
      metadata: {
        ...currentThread.metadata,
        label: `[archived] ${currentLabel}`,
      },
    };

    try {
      const response = await fetch(`${AI_API_URL}/threads/${currentThread.thread_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateBody),
      });

      if (response.ok) {
        // Refresh threads - this will filter out the archived thread
        await fetchThreads();
      } else {
        const errorText = await response.text();
        console.error('Failed to archive thread:', errorText);
      }
    } catch (error) {
      console.error('Error archiving thread:', error);
    }
  };

  // Change to single selection instead of array
  // const [selectedWorkspace, setSelectedWorkspace] = useState<string | null>(null);

  // Move this declaration above all useEffect hooks that reference selectedWorkspace

  const handleResetLocation = () => {
    setLayersSearch({ search: '' });
    setPlacesSearch({ search: '', filters: {} });
    setSidebarPanel(EPanels.PLACES);
    resetLayers();
    //resetMap();
  };

  const onOrgChange = (org) => {
    // console.log('Header onOrgChange called with:', org);

    // Only update app-specific state, not workspace
    const workspacesArray = [org];
    if (org !== 'common') {
      workspacesArray.push('common');
    }

    setWorkspaces(workspacesArray);
    setPlacesSearch({
      search: '',
      filters: {},
    });
    setLayersSearch({
      search: '',
      filters: {},
    });
  };

  const classes = useStyles();

  // Add this useEffect to handle the rename dialog trigger
  useEffect(() => {
    if (shouldOpenRenameDialog && selectedThread) {
      const thread = threads.find((t) => t.thread_id === selectedThread);
      if (thread) {
        setEditThread(thread);
        setNewLabel(thread.label || '');
        setIsEditPopoverOpen(true);
        // Set anchor element to the AppContextSwitcher button for positioning
        const contextSwitcherButton = document.querySelector('.marapp-qa-context-switcher button');
        setEditAnchorEl(contextSwitcherButton as HTMLElement);
      }
      setShouldOpenRenameDialog(false); // Reset the trigger
    }
  }, [shouldOpenRenameDialog, selectedThread, threads]);

  // Add this useEffect to track selectedWorkspace changes
  useEffect(() => {
    // console.log('Header useEffect - selectedWorkspace changed to:', selectedWorkspace);
  }, [selectedWorkspace]);

  // Move the logo calculation inside a useMemo to ensure it updates when currentWorkspace changes
  const logoSrc = useMemo(() => {
    // console.log('Header useMemo - calculating logo for workspace:', currentWorkspace);
    const logo = getWorkspaceLogo(currentWorkspace);
    // console.log('Header useMemo - calculated logoSrc:', logo);
    return logo;
  }, [currentWorkspace]);

  // Replace the existing selectedWorkspace initialization useEffect with this improved version:
  // useEffect(() => {
  //   const userOrg = (userData as IUser)?.organization;

  //   console.log('Header initialization - userData:', userData);
  //   console.log('Header initialization - userOrg:', userOrg);
  //   console.log('Header initialization - current selectedWorkspace:', selectedWorkspace);
  //   console.log('Header initialization - isAuthenticated:', isAuthenticated);

  //   // Update selectedWorkspace whenever Auth0 context changes and we have valid user data
  //   if (isAuthenticated && userOrg) {
  //     // Only update if the workspace actually changed or if it's not set yet
  //     if (selectedWorkspace !== userOrg) {
  //       console.log('Header - Updating selectedWorkspace from', selectedWorkspace, 'to:', userOrg);

  //       setSelectedWorkspace(userOrg);

  //       const workspacesArray = [userOrg];
  //       if (userOrg !== 'common') {
  //         workspacesArray.push('common');
  //       }

  //       setWorkspaces(workspacesArray);
  //       setupUserOrg(workspacesArray);

  //       console.log('Header - Updated workspacesArray:', workspacesArray);
  //     }
  //   }
  //   // If user is not authenticated, clear the workspace
  //   else if (!isAuthenticated && selectedWorkspace) {
  //     console.log('Header - Clearing selectedWorkspace due to logout');
  //     setSelectedWorkspace(null);
  //   }
  // }, [
  //   isAuthenticated,
  //   (userData as IUser)?.organization,
  //   selectedWorkspace,
  //   setSelectedWorkspace,
  //   setWorkspaces,
  //   setupUserOrg,
  // ]);

  // Handler to open dialog
  const handleOpenOrgDialog = () => {
    setShouldOpenOrgDialog(true);
    // Anchor to context switcher button
    const contextSwitcherButton = document.querySelector('.marapp-qa-context-switcher button');
    setOrgAnchorEl(contextSwitcherButton as HTMLElement);

    // Fix: Get the current thread and its organization
    const currentThread = threads.find((t) => t.thread_id === selectedThread);
    setOrgToSet((currentThread && currentThread.organization) || '');
  };

  // Handler to save organization
  const handleOrgSave = async () => {
    const currentThread = threads.find((t) => t.thread_id === selectedThread);
    if (!currentThread) return;

    const updateOrgBody = {
      metadata: { ...currentThread.metadata, organization: orgToSet },
    };

    // console.log(
    //   '🔍 updateThreadOrg - Request URL:',
    //   `${AI_API_URL}/threads/${currentThread.thread_id}`
    // );
    // console.log('🔍 updateThreadOrg - Request body:', JSON.stringify(updateOrgBody, null, 2));

    const response = await fetch(`${AI_API_URL}/threads/${currentThread.thread_id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateOrgBody),
    });

    // console.log('🔍 updateThreadOrg - Response status:', response.status);
    // console.log(
    //   '🔍 updateThreadOrg - Response headers:',
    //   Object.fromEntries(response.headers.entries())
    // );

    if (response.ok) {
      const responseData = await response.json();
      // console.log('🔍 updateThreadOrg - Response data:', JSON.stringify(responseData, null, 2));
    } else {
      const errorText = await response.text();
      console.error('🔍 updateThreadOrg - Error response:', errorText);
    }

    setShouldOpenOrgDialog(false);
    setOrgAnchorEl(null);
    fetchThreads();
  };

  // Add logging to see what's happening with the logo
  // const logoSrcDebug = getWorkspaceLogo(currentWorkspace);
  // console.log('Header component - selectedWorkspace (array):', selectedWorkspace);
  // console.log('Header component - currentWorkspace (string):', currentWorkspace);
  // console.log('Header component - logoSrc:', logoSrcDebug);

  return (
    <>
      <div
        className="custom-header-container"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: 56,
          padding: '6px 12px',
          boxSizing: 'border-box',
        }}
      >
        {/* Logo */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            paddingRight: '8px',
            height: '100%',
          }}
        >
          <img
            key={currentWorkspace}
            src={logoSrc}
            alt={MAP_APP_NAME}
            onClick={handleResetLocation}
            onLoad={(e) => {
              const imgElement = e.currentTarget as HTMLImageElement;
              // console.log('Image loaded successfully for workspace:', currentWorkspace);
              // console.log('Image src:', imgElement.src);
              // console.log(
              //   'Image natural dimensions:',
              //   imgElement.naturalWidth,
              //   'x',
              //   imgElement.naturalHeight
              // );
              // console.log(
              //   'Image computed dimensions:',
              //   imgElement.offsetWidth,
              //   'x',
              //   imgElement.offsetHeight
              // );
              // console.log('Image element:', imgElement);

              // Check if image has zero dimensions
              if (imgElement.naturalWidth === 0 || imgElement.naturalHeight === 0) {
                console.error('Image loaded but has zero dimensions, falling back to default');
                imgElement.src = APP_LOGO;
              }
            }}
            onError={(e) => {
              const imgElement = e.target as HTMLImageElement;
              console.error('Image failed to load for workspace:', currentWorkspace);
              console.error('Failed src:', imgElement.src);
              console.error('Error event:', e);
              // Fallback to default logo
              e.currentTarget.src = APP_LOGO;
            }}
            style={{
              // Force specific dimensions for SVGs
              height: currentWorkspace === 'steve' ? '36px' : 'auto',
              width: currentWorkspace === 'steve' ? 'auto' : 'auto',
              minHeight: '24px',
              minWidth: '60px',
              maxHeight: '36px',
              maxWidth: '128px',
              marginRight: 4,
              objectFit: 'contain',
              cursor: 'pointer',
              display: 'block',
              // Remove debug styling
              border: 'none',
              // Ensure the image is visible
              opacity: 1,
              visibility: 'visible',
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            height: '100%',
          }}
        >
          {/* Remove the Radio buttons from here - moved to AppContextSwitcher */}

          <Select
            //value={selectedThread || (threads[0] && threads[0].thread_id) || ""}
            value={selectValue}
            onOpen={(event) => {
              console.log('🔽 Header: Thread dropdown opened');
              setIsChatModalOpen(true);
            }}
            onClose={(event) => setIsChatModalOpen(false)}
            onChange={(event) => {
              // Only handle thread selection, not the rename option
              const newThreadId = event.target.value;
              if (newThreadId && newThreadId !== selectedThread) {
                console.log('🔄 Header: Switching threads');
                console.log('  From:', selectedThread);
                console.log('  To:', newThreadId);

                // Switch to new thread
                setThreadIdInUrl(newThreadId);
                handleThreadChange(event);
                let thread = threads.find((t) => t.thread_id === newThreadId);
                if (thread && thread.href) {
                  console.log('  🔄 Restoring new thread state:', thread.href);
                  handleClick(new URL(thread.href));
                } else {
                  console.warn('  ⚠️ New thread has no href to restore:', thread);
                }
                setIsChatModalOpen(false);
              } else if (newThreadId === selectedThread) {
                console.log('  ℹ️ Same thread selected, not restoring state');
                setIsChatModalOpen(false);
              }
            }}
            displayEmpty
            style={{
              minWidth: 264,
              maxWidth: 264,
              height: 36, // match logo and IconButton
              marginRight: 2,
            }}
            inputProps={{
              style: { paddingTop: 6, paddingBottom: 6, height: 36 },
            }}
            MenuProps={{
              PaperProps: { style: { marginTop: 6 } },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              getContentAnchorEl: null, // Important for correct positioning in MUI v4
            }}
          >
            {threads.map((thread) => (
              <MenuItem key={thread.thread_id} value={thread.thread_id}>
                {thread.label || thread.created_at}
              </MenuItem>
            ))}
          </Select>

          {/* Popover for editing: */}
          <Popover
            open={Boolean(editAnchorEl)}
            anchorEl={editAnchorEl}
            onClose={handleEditClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <div style={{ padding: 20, background: '#455A64', color: '#fff', minWidth: 280 }}>
              {editThread && (
                <>
                  <TextField
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    fullWidth
                    placeholder="Enter chat name..."
                    style={{ marginBottom: 20 }}
                    InputProps={{
                      style: {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                      },
                    }}
                  />

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: '0.85em', color: '#a9a9a9', marginBottom: 8 }}>
                      Thread ID:{' '}
                      <span style={{ fontFamily: 'monospace', color: '#fff' }}>
                        {editThread.thread_id}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85em', color: '#a9a9a9', marginBottom: 8 }}>
                      Created:{' '}
                      <span style={{ fontFamily: 'monospace', color: '#fff' }}>
                        {editThread.created_at}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85em', color: '#a9a9a9' }}>
                      Updated:{' '}
                      <span style={{ fontFamily: 'monospace', color: '#fff' }}>
                        {editThread.updated_at}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    <Button
                      onClick={handleEditClose}
                      variant="outlined"
                      style={{
                        color: '#a9a9a9',
                        borderColor: '#a9a9a9',
                        background: 'transparent',
                        textTransform: 'none',
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleEditSave}
                      color="primary"
                      variant="contained"
                      style={{
                        color: '#455A64',
                        textTransform: 'none',
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Popover>

          <Tooltip title="New Chat" arrow>
            <IconButton
              color="primary"
              onClick={handleCreateThread}
              size="small"
              aria-label="Start a new chat"
              style={{ height: 36, width: 36, marginRight: 2, borderRadius: 8 }}
            >
              <ChatBubbleOutlineIcon />
            </IconButton>
          </Tooltip>
        </div>

        <div style={{ textAlign: 'right' }}>
          <AppContextSwitcher
            label={t('More Options')}
            checkedCount={1}
            renderButton={true}
            renderDropdown={true}
            onChange={(g) => {
              if (g === 'rename-chat') {
                setShouldOpenRenameDialog(true);
              } else if (g === 'change-org') {
                handleOpenOrgDialog();
              } else if (g === 'archive-chat') {
                handleArchiveThread();
              } else if (g === 'toggle-archived') {
                const newValue = !hideArchived;
                setHideArchived(newValue);
                fetchThreads(currentWorkspace, newValue);
              } else if (g && g !== 'map-view') {
                onOrgChange(g);
              } else if (g === 'map-view') {
                window.location.assign(`${MAP_ADMIN_URL}${g}`);
              }
            }}
            handleResetLocation={handleResetLocation}
          >
            {selectedThread && [
              <Option
                key="rename-chat"
                value="rename-chat"
                onClick={() => {
                  setShouldOpenRenameDialog(true);
                }}
                divider={true}
              >
                <ListItemText primary={t('Rename')} primaryTypographyProps={{ variant: 'body2' }} />
                <ChatIcon fontSize="small" style={{ marginLeft: 8 }} />
              </Option>,
              <Option
                key="change-org"
                value="change-org"
                onClick={handleOpenOrgDialog}
                divider={true}
              >
                <ListItemText primary={t('Move')} primaryTypographyProps={{ variant: 'body2' }} />
                <SwapHorizIcon fontSize="small" style={{ marginLeft: 8 }} />
              </Option>,
              <Option
                key="archive-chat"
                value="archive-chat"
                onClick={handleArchiveThread}
                divider={true}
              >
                <ListItemText
                  primary={t('Archive')}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
                <ArchiveIcon fontSize="small" style={{ marginLeft: 8 }} />
              </Option>,
            ]}

            {/* Show archived checkbox option */}
            <Option key="toggle-archived" value="toggle-archived" divider={true}>
              <ListItemText
                primary={t('Show archived chats')}
                primaryTypographyProps={{ variant: 'body2' }}
              />
              <Checkbox
                checked={!hideArchived}
                size="small"
                color="primary"
                style={{ marginLeft: 8, padding: 0 }}
              />
            </Option>

            {COMPANY_ABOUT_URL ? (
              <Option
                value="map-view"
                // divider={true}
                component="a"
                href={COMPANY_ABOUT_URL}
                title={MAP_APP_NAME}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ListItemText primary={t('Home')} primaryTypographyProps={{ variant: 'body2' }} />
              </Option>
            ) : null}

            {COMPANY_SUPPORT_URL ? (
              <Option
                value="map-view"
                component="a"
                href={COMPANY_SUPPORT_URL}
                title={MAP_APP_NAME}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ListItemText
                  primary={t('Support')}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </Option>
            ) : null}

            {/* Remove this line to hide workspace selector from header menu */}
            {/* {isAuthenticated && orgCheckBoxes} */}
          </AppContextSwitcher>
        </div>
      </div>

      {/* Popover/Dialog for changing organization */}
      <Popover
        open={shouldOpenOrgDialog}
        anchorEl={orgAnchorEl}
        onClose={() => setShouldOpenOrgDialog(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div style={{ padding: 20, background: '#455A64', color: '#fff', minWidth: 320 }}>
          <Typography variant="subtitle1" style={{ marginBottom: 16 }}>
            {t('Change chat workspace')}
          </Typography>

          {/* Show current workspace */}
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 4,
            }}
          >
            <Typography variant="body2" style={{ color: '#a9a9a9', marginBottom: 4 }}>
              Current workspace:
            </Typography>
            <Typography variant="body1" style={{ color: '#fff', fontWeight: 500 }}>
              {(() => {
                const currentThread = threads.find((t) => t.thread_id === selectedThread);
                return currentThread?.organization || currentWorkspace || 'Unknown';
              })()}
            </Typography>
          </div>

          <Typography variant="body2" style={{ marginBottom: 12, color: '#a9a9a9' }}>
            Select new workspace:
          </Typography>
          <Select
            value={orgToSet}
            onChange={(e) => setOrgToSet(e.target.value as string)}
            fullWidth
            style={{ marginBottom: 20 }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              <em>Choose a workspace...</em>
            </MenuItem>
            {availableGroups.map((g) => (
              <MenuItem key={g.name} value={g.name}>
                <div>
                  <div>{g.name}</div>
                  <div style={{ fontSize: '0.75em', color: '#666' }}>
                    {g.layers !== 'N/A' && `${g.layers} layers`}
                    {g.locations !== 'N/A' && g.layers !== 'N/A' && ' • '}
                    {g.locations !== 'N/A' && `${g.locations} locations`}
                  </div>
                </div>
              </MenuItem>
            ))}
          </Select>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button
              onClick={() => {
                setShouldOpenOrgDialog(false);
                setOrgAnchorEl(null);
                setOrgToSet('');
              }}
              variant="outlined"
              style={{
                color: '#a9a9a9',
                borderColor: '#a9a9a9',
                background: 'transparent',
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleOrgSave}
              color="primary"
              variant="contained"
              style={{
                color: '#455A64',
                textTransform: 'none',
              }}
              disabled={
                !orgToSet ||
                orgToSet ===
                  (() => {
                    const currentThread = threads.find((t) => t.thread_id === selectedThread);
                    return currentThread?.organization || currentWorkspace;
                  })()
              }
            >
              Save
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default Header;
